import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface GraphNode {
  id: string;
  label: string;
  llm: 'yes' | 'no' | 'mixed';
  description: string;
}

export interface GraphEdge {
  from: string;
  to: string;
  payload: string;
}

export interface PipelineGraph {
  id: 'setup' | 'runtime';
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface AgentRun {
  id: string;
  projectId: string;
  pipeline: 'setup' | 'runtime';
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
}

export interface PipelineEvent {
  runId: string;
  projectId: string;
  nodeId: string;
  agentName: string;
  eventType: 'started' | 'finished' | 'failed' | 'skipped';
  durationMs?: number;
  errorMessage?: string;
  at: string;
}

export interface PersistedPipelineEvent extends PipelineEvent {
  id: string;
  input: unknown;
  output: unknown;
  /** Agent Inspector (v14): the FULL data the agent's fn was invoked
   *  with. Distinct from the compact `input` summary shown in the
   *  inline run trace. */
  assembledInput?: unknown;
  createdAt: string;
}

export interface LlmCall {
  id: string;
  runId: string | null;
  projectId: string;
  nodeId: string;
  kind: 'complete' | 'embed' | 'tts' | 'stt';
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  usdCost: string;
  latencyMs: number;
  /** Agent Inspector (v14): literal LLM exchange. Null on embed/tts/stt. */
  systemPrompt?: string | null;
  userPrompt?: string | null;
  rawResponse?: string | null;
  createdAt: string;
}

export function useGraph(id: 'setup' | 'runtime' | 'reel' = 'runtime') {
  return useQuery({
    queryKey: ['pipeline', 'graph', id],
    queryFn: () => api<{ graph: PipelineGraph }>(`/api/pipeline/graph?id=${id}`).then((r) => r.graph),
    staleTime: Infinity,
  });
}

/** All graph nodes across all three pipelines, indexed by node id. Used
 *  by the Agent Inspector to look up the GraphNode metadata for any
 *  event regardless of which pipeline it came from. */
export function useAllGraphNodes() {
  return useQuery({
    queryKey: ['pipeline', 'graph-nodes-all'],
    queryFn: async () => {
      const [setup, runtime, reel] = await Promise.all([
        api<{ graph: PipelineGraph }>('/api/pipeline/graph?id=setup'),
        api<{ graph: PipelineGraph }>('/api/pipeline/graph?id=runtime'),
        api<{ graph: PipelineGraph }>('/api/pipeline/graph?id=reel'),
      ]);
      const index: Record<string, GraphNode> = {};
      for (const g of [setup.graph, runtime.graph, reel.graph]) {
        for (const node of g.nodes) {
          index[node.id] = node;
        }
      }
      return index;
    },
    staleTime: Infinity,
  });
}

export function useRuns(projectId: string | undefined) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ['pipeline', 'runs', projectId],
    queryFn: () =>
      api<{ runs: AgentRun[]; monthlySpendUsd: number }>(`/api/pipeline/${projectId}/runs`),
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => r.status === 'running') ? 2000 : false,
  });
}

export function useRunDetails(projectId: string | undefined, runId: string | undefined, enabled = true) {
  return useQuery({
    enabled: !!projectId && !!runId && enabled,
    queryKey: ['pipeline', 'runs', projectId, runId],
    queryFn: async () => {
      const data = await api<{ run: AgentRun; events: Array<Omit<PersistedPipelineEvent, 'at'> & { createdAt: string }>; calls: LlmCall[] }>(
        `/api/pipeline/${projectId}/runs/${runId}`,
      );
      return {
        ...data,
        events: data.events.map((event) => ({
          ...event,
          at: event.createdAt,
        })) as PersistedPipelineEvent[],
      };
    },
  });
}

export interface MusicTrackOption {
  id: string;
  label: string;
  moods: string[];
  approxDurationSec: number;
  fileName: string;
  attribution?: string;
}

/** Curated music library for the music-mode reel pipeline. Static
 *  across the app session — stale time set to Infinity. */
export function useMusicLibrary() {
  return useQuery({
    queryKey: ['music-library'],
    queryFn: () => api<{ tracks: MusicTrackOption[] }>('/api/pipeline/music-library').then((r) => r.tracks),
    staleTime: Infinity,
  });
}

export function useTriggerRun(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: {
      hint?: string;
      format?: 'carousel' | 'single_image' | 'reel_vertical';
      /** Reel-only test cap (3-60s). When set, produces a short reel for
       *  fast iteration on the visual layer. Off → normal full-length. */
      testSeconds?: number;
      /** Reel-only audio mode override. Defaults to the brand kit's setting. */
      audioMode?: 'vo' | 'music';
      /** Reel-only music track id override (must exist in MUSIC_LIBRARY). */
      musicTrackId?: string;
    }) =>
      api<{ runId: string; remaining: number }>(`/api/pipeline/${projectId}/runs`, {
        method: 'POST',
        body: JSON.stringify({
          hint: input?.hint?.trim() || undefined,
          format: input?.format,
          testSeconds: input?.testSeconds,
          audioMode: input?.audioMode,
          musicTrackId: input?.musicTrackId,
        }),
      }),
    onSuccess: () => {
      // Refresh runs list immediately; live events will arrive via SSE.
      qc.invalidateQueries({ queryKey: ['pipeline', 'runs', projectId] });
    },
  });
}

export type NodeStatus = 'idle' | 'running' | 'done' | 'failed';

const MAX_EVENTS_BUFFER = 200;

export function useLivePipeline(projectId: string | undefined) {
  const [nodeStatus, setNodeStatus] = useState<Record<string, NodeStatus>>({});
  const [events, setEvents] = useState<PipelineEvent[]>([]);
  const currentRunIdRef = useRef<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const es = new EventSource(`/api/events/${projectId}`, { withCredentials: true });
    esRef.current = es;
    es.addEventListener('pipeline', (msg) => {
      try {
        const evt = JSON.parse((msg as MessageEvent).data) as PipelineEvent;

        // Detect a new run starting (first 'started' event of a new runId)
        // and reset graph node colours so we don't carry stale 'done' states
        // from the previous run.
        if (evt.runId !== currentRunIdRef.current && evt.eventType === 'started') {
          currentRunIdRef.current = evt.runId;
          setNodeStatus({ [evt.nodeId]: 'running' });
        } else {
          setNodeStatus((prev) => ({
            ...prev,
            [evt.nodeId]:
              evt.eventType === 'started'
                ? 'running'
                : evt.eventType === 'finished'
                  ? 'done'
                  : evt.eventType === 'failed'
                    ? 'failed'
                    : 'idle',
          }));
        }

        setEvents((prev) => {
          const next = [...prev, evt];
          return next.length > MAX_EVENTS_BUFFER ? next.slice(-MAX_EVENTS_BUFFER) : next;
        });
      } catch (err) {
        console.error('SSE parse error', err);
      }
    });
    es.onerror = () => {
      // Browser will auto-reconnect; nothing to do.
    };
    return () => {
      es.close();
      esRef.current = null;
    };
  }, [projectId]);

  const lastEvent = events.length > 0 ? events[events.length - 1]! : null;

  return { nodeStatus, lastEvent, events };
}
